import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Users } from 'lucide-react';
import { callAdminGetProductOperations } from '../../services/admin';

type ProductId = 'staffaxis' | 'noteaxis';

type Props = {
  productId: ProductId;
};

const productMeta: Record<ProductId, { title: string }> = {
  staffaxis: { title: 'StaffAxis' },
  noteaxis: { title: 'NoteAxis' },
};

const statusPill = (value: string) => {
  const v = (value || '').toLowerCase();
  if (v === 'active' || v === 'trialing') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
  if (v === 'past_due' || v === 'canceled') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
  return 'bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300';
};

const AdminProductUsers: React.FC<Props> = ({ productId }) => {
  const meta = productMeta[productId];
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await callAdminGetProductOperations({ productId, limitUsers: 2000 });
        if (!mounted) return;
        setUsers(result.users || []);
      } catch (err: any) {
        if (!mounted) return;
        setError(err?.message || 'Failed to load users');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [productId]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      [user.uid, user.email, user.name, user.role, user?.subscription?.planId, user?.subscription?.status]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [search, users]);

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to={`/admin/${productId}`} className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to {meta.title} Ops
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary-500/10 dark:bg-primary-400/10">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{meta.title} Users</h1>
              <p className="text-slate-600 dark:text-gray-400">User records linked to {meta.title}</p>
            </div>
          </div>
        </motion.div>

        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass mb-6" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users by email, uid, plan, status"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/50 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </motion.div>

        {error && <div className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</div>}

        <motion.div className="glass-effect rounded-2xl p-4 shadow-glass" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          {loading ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">Loading users...</p>
          ) : filteredUsers.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-gray-400">No users found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-gray-700">
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">UID</th>
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3">Plan</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.uid} className="border-b border-slate-100 dark:border-gray-800">
                      <td className="py-2 pr-3">{user.email || user.name || 'Unknown'}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{user.uid}</td>
                      <td className="py-2 pr-3">{user.role || '—'}</td>
                      <td className="py-2 pr-3 capitalize">{user?.subscription?.planId || '—'}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(user?.subscription?.status || 'none')}`}>
                          {user?.subscription?.status || 'none'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminProductUsers;
